import type { CSSProperties } from 'react';
import { readSceneLayerToggle } from '../../../lib/modelViewerSceneLayerRegistry';
import { isSectionBackdropQuarantined } from '../../../lib/sectionBackdropQuarantine';

export type SectionSceneLayerMode = 'auto' | 'block' | 'section-backdrop';

const LEGACY_WALL_TEXTURE_SRC = '/holding-scene-wall-texture.svg';
const DEFAULT_WALL_TEXTURE_SRC = '/holding-scene-wall-plaster.svg';
const DEFAULT_SYMBOL_OVERLAY_SRC = '/BGG.svg';
const DEFAULT_BAKED_SYMBOL_OVERLAY_SRC = '/BGG.svg';
const DEFAULT_SCENE_HORIZON_Y = 76;

export const MODEL_VIEWER_SCENE_RESPONSIVE_KEYS = [
  'sectionSceneHorizonY',
  'sectionBacklightYOffset',
  'sectionWallLightYOffset',
  'sectionBacklightX',
  'sectionBacklightY',
  'sectionWallLightX',
  'sectionArtX',
  'sectionArtY',
] as const;

export type ModelViewerSceneViewport = 'desktop' | 'tablet' | 'mobile';

export function resolveModelViewerSceneContentForViewport(
  content: Record<string, unknown>,
  viewport: ModelViewerSceneViewport = 'desktop'
): Record<string, unknown> {
  if (viewport === 'desktop') return content;
  const root = content.responsiveSceneOverrides;
  if (!root || typeof root !== 'object' || Array.isArray(root)) return content;
  const patch = (root as Record<string, unknown>)[viewport];
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return content;
  return { ...content, ...(patch as Record<string, unknown>) };
}

export type WallTexturePresetId =
  | 'custom'
  | 'dark-plaster'
  | 'smoked-plaster'
  | 'aged-stone'
  | 'charcoal-wall'
  | 'burnished-clay'
  | 'subtle-fibre';

export type WallTexturePreset = {
  id: WallTexturePresetId;
  label: string;
  description: string;
  textureSrc: string;
  opacity: number;
  blendMode: CSSProperties['mixBlendMode'];
  repeatMode: 'cover' | 'repeat';
  scale: number;
  brightness: number;
  contrast: number;
  tint: string;
};

export type SymbolFamilyPresetId =
  | 'custom'
  | 'bgg-premium'
  | 'folklore-mixed'
  | 'celestial-astrolabe'
  | 'ocean-tide'
  | 'wind-cloud'
  | 'sun-forge'
  | 'divine-wings-lotus'
  | 'earth-temple';

export type SymbolFamilyPreset = {
  id: SymbolFamilyPresetId;
  label: string;
  description: string;
  symbolSrc: string;
  tint: string;
  opacity: number;
  scale: number;
  blendMode: CSSProperties['mixBlendMode'];
  renderMode?: SymbolRenderMode;
  bakedOverlaySrc?: string;
};

export type SymbolEffectPresetId =
  | 'flat'
  | 'soft-glow'
  | 'embossed-wall'
  | 'engraved-pressed'
  | 'raised-gilded'
  | 'ancient-carved';

export type SymbolRenderMode = 'vector-mask' | 'baked-overlay';

export const WALL_TEXTURE_PRESETS: WallTexturePreset[] = [
  {
    id: 'dark-plaster',
    label: 'Dark plaster',
    description: 'Low-contrast smoky plaster for the default gallery wall.',
    textureSrc: DEFAULT_WALL_TEXTURE_SRC,
    opacity: 0.1,
    blendMode: 'soft-light',
    repeatMode: 'cover',
    scale: 146,
    brightness: 0.78,
    contrast: 1.12,
    tint: '#a47638',
  },
  {
    id: 'smoked-plaster',
    label: 'Smoked plaster',
    description: 'Softer smoke movement with less grain and a darker centre.',
    textureSrc: '/holding-wall-smoked-plaster.svg',
    opacity: 0.12,
    blendMode: 'soft-light',
    repeatMode: 'cover',
    scale: 142,
    brightness: 0.72,
    contrast: 1.18,
    tint: '#9f7a4d',
  },
  {
    id: 'aged-stone',
    label: 'Aged stone',
    description: 'Subtle mineral mottling with a cooler gallery-wall feel.',
    textureSrc: '/holding-wall-aged-stone.svg',
    opacity: 0.1,
    blendMode: 'overlay',
    repeatMode: 'cover',
    scale: 150,
    brightness: 0.7,
    contrast: 1.22,
    tint: '#9b8a72',
  },
  {
    id: 'charcoal-wall',
    label: 'Charcoal wall',
    description: 'Deep matte charcoal with gentle cloudy texture.',
    textureSrc: '/holding-wall-charcoal.svg',
    opacity: 0.14,
    blendMode: 'soft-light',
    repeatMode: 'cover',
    scale: 145,
    brightness: 0.68,
    contrast: 1.1,
    tint: '#6d6a62',
  },
  {
    id: 'burnished-clay',
    label: 'Burnished clay',
    description: 'Warmer brown-gold wall tone for amber-heavy colourways.',
    textureSrc: '/holding-wall-burnished-clay.svg',
    opacity: 0.12,
    blendMode: 'soft-light',
    repeatMode: 'cover',
    scale: 148,
    brightness: 0.74,
    contrast: 1.2,
    tint: '#a86f3e',
  },
  {
    id: 'subtle-fibre',
    label: 'Subtle fibre/paper',
    description: 'Fine paper-like grain, useful when symbols need to stay clean.',
    textureSrc: '/holding-wall-subtle-fibre.svg',
    opacity: 0.08,
    blendMode: 'soft-light',
    repeatMode: 'cover',
    scale: 140,
    brightness: 0.82,
    contrast: 1.08,
    tint: '#a58a65',
  },
];

export const CUSTOM_WALL_TEXTURE_PRESET_ID: WallTexturePresetId = 'custom';

export const SYMBOL_FAMILY_PRESETS: SymbolFamilyPreset[] = [
  {
    id: 'bgg-premium',
    label: 'BGG premium',
    description: 'Final painted wall symbols from the BGG artwork (PNG or SVG baked overlay).',
    symbolSrc: '/BGG.svg',
    bakedOverlaySrc: '/BGG.svg',
    renderMode: 'baked-overlay',
    tint: '#a9793e',
    opacity: 0.92,
    scale: 132,
    blendMode: 'screen',
  },
  {
    id: 'folklore-mixed',
    label: 'Folklore mixed',
    description: 'A restrained mix of celestial, tide, wind, and temple geometry.',
    symbolSrc: DEFAULT_SYMBOL_OVERLAY_SRC,
    tint: '#9c5f28',
    opacity: 0.36,
    scale: 136,
    blendMode: 'screen',
  },
  {
    id: 'celestial-astrolabe',
    label: 'Celestial / astrolabe',
    description: 'Orbital rings, star markers, and navigational geometry.',
    symbolSrc: '/holding-symbols-celestial.svg',
    tint: '#a9793e',
    opacity: 0.34,
    scale: 138,
    blendMode: 'screen',
  },
  {
    id: 'ocean-tide',
    label: 'Ocean / tide',
    description: 'Wave arcs and tide paths for water-led mythology.',
    symbolSrc: '/holding-symbols-ocean.svg',
    tint: '#8a7654',
    opacity: 0.34,
    scale: 140,
    blendMode: 'soft-light',
  },
  {
    id: 'wind-cloud',
    label: 'Wind / cloud',
    description: 'Curved air trails and cloud cartography.',
    symbolSrc: '/holding-symbols-wind.svg',
    tint: '#8f7d63',
    opacity: 0.32,
    scale: 140,
    blendMode: 'soft-light',
  },
  {
    id: 'sun-forge',
    label: 'Sun / forge',
    description: 'Solar rays, forge marks, and fire geometry.',
    symbolSrc: '/holding-symbols-sun-forge.svg',
    tint: '#ad6f2f',
    opacity: 0.34,
    scale: 136,
    blendMode: 'screen',
  },
  {
    id: 'divine-wings-lotus',
    label: 'Divine / wings / lotus',
    description: 'Winged arcs, lotus forms, and sacred symmetry.',
    symbolSrc: '/holding-symbols-divine.svg',
    tint: '#9f7443',
    opacity: 0.32,
    scale: 134,
    blendMode: 'screen',
  },
  {
    id: 'earth-temple',
    label: 'Earth / temple geometry',
    description: 'Stepped temple, stone, and earth-aligned geometry.',
    symbolSrc: '/holding-symbols-earth.svg',
    tint: '#9b744f',
    opacity: 0.34,
    scale: 138,
    blendMode: 'soft-light',
  },
];

export const SYMBOL_EFFECT_PRESETS: Array<{ value: SymbolEffectPresetId; label: string }> = [
  { value: 'flat', label: 'Flat' },
  { value: 'soft-glow', label: 'Soft glow' },
  { value: 'embossed-wall', label: 'Embossed wall' },
  { value: 'engraved-pressed', label: 'Engraved / pressed' },
  { value: 'raised-gilded', label: 'Raised gilded' },
  { value: 'ancient-carved', label: 'Ancient carved' },
];

export const CUSTOM_SYMBOL_FAMILY_PRESET_ID: SymbolFamilyPresetId = 'custom';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const finiteNumber = (value: unknown, fallback: number) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const hasFiniteNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed);
};

const nonEmptyString = (value: unknown, fallback: string) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
};

const safeCssUrl = (value: string) => value.replace(/["\\]/g, '');

const colorWithAlpha = (color: string, alpha: number) => {
  const clampedAlpha = clamp(alpha, 0, 1);
  const trimmed = color.trim();
  const hex = trimmed.match(/^#?([0-9a-f]{6})$/i)?.[1];
  if (!hex) return `rgba(165, 138, 101, ${clampedAlpha})`;
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

const resolveWallTextureSrc = (value: unknown) => {
  const resolved = nonEmptyString(value, DEFAULT_WALL_TEXTURE_SRC);
  return resolved === LEGACY_WALL_TEXTURE_SRC ? DEFAULT_WALL_TEXTURE_SRC : resolved;
};

export const findWallTexturePreset = (value: unknown) => {
  if (typeof value !== 'string') return WALL_TEXTURE_PRESETS[0];
  return WALL_TEXTURE_PRESETS.find((preset) => preset.id === value) ?? WALL_TEXTURE_PRESETS[0];
};

export const findSymbolFamilyPreset = (value: unknown) => {
  if (typeof value !== 'string') return SYMBOL_FAMILY_PRESETS[0];
  return SYMBOL_FAMILY_PRESETS.find((preset) => preset.id === value) ?? SYMBOL_FAMILY_PRESETS[0];
};

const resolveWallTexturePresetId = (content: Record<string, unknown>): WallTexturePresetId => {
  const raw = content.sectionWallTexturePreset;
  if (raw === CUSTOM_WALL_TEXTURE_PRESET_ID) return CUSTOM_WALL_TEXTURE_PRESET_ID;
  return findWallTexturePreset(raw).id;
};

const resolveSymbolFamilyPresetId = (content: Record<string, unknown>): SymbolFamilyPresetId => {
  const raw = content.sectionSymbolFamilyPreset;
  if (raw === CUSTOM_SYMBOL_FAMILY_PRESET_ID) return CUSTOM_SYMBOL_FAMILY_PRESET_ID;
  return findSymbolFamilyPreset(raw).id;
};

const resolveSymbolEffectPresetId = (value: unknown): SymbolEffectPresetId => {
  if (
    value === 'soft-glow' ||
    value === 'embossed-wall' ||
    value === 'engraved-pressed' ||
    value === 'raised-gilded' ||
    value === 'ancient-carved'
  ) {
    return value;
  }
  return 'flat';
};

const resolveSymbolRenderMode = (value: unknown): SymbolRenderMode =>
  value === 'baked-overlay' ? 'baked-overlay' : 'vector-mask';

function resolveWallTextureRepeatMode(
  content: Record<string, unknown>,
  preset: WallTexturePreset,
  usePreset: boolean
): 'cover' | 'repeat' {
  const requestedRepeat = content.sectionWallTextureRepeatMode === 'repeat';
  if (!requestedRepeat) {
    return usePreset ? preset.repeatMode : 'cover';
  }
  return !usePreset || preset.repeatMode === 'repeat' ? 'repeat' : 'cover';
}

export const resolveWallTexturePresetPatch = (presetId: WallTexturePresetId) => {
  if (presetId === CUSTOM_WALL_TEXTURE_PRESET_ID) {
    return { sectionWallTexturePreset: CUSTOM_WALL_TEXTURE_PRESET_ID };
  }
  const preset = findWallTexturePreset(presetId);
  return {
    sectionWallTexturePreset: preset.id,
    sectionWallTextureSrc: preset.textureSrc,
    sectionWallTextureOpacity: preset.opacity,
    sectionArtBlendMode: preset.blendMode,
    sectionWallTextureRepeatMode: preset.repeatMode,
    sectionWallTextureScale: preset.scale,
    sectionWallTextureBrightness: preset.brightness,
    sectionWallTextureContrast: preset.contrast,
    sectionArtTint: preset.tint,
  };
};

export const resolveSymbolFamilyPresetPatch = (presetId: SymbolFamilyPresetId) => {
  if (presetId === CUSTOM_SYMBOL_FAMILY_PRESET_ID) {
    return { sectionSymbolFamilyPreset: CUSTOM_SYMBOL_FAMILY_PRESET_ID };
  }
  const preset = findSymbolFamilyPreset(presetId);
  return {
    sectionSymbolFamilyPreset: preset.id,
    sectionSymbolOverlaySrc: preset.symbolSrc,
    sectionSymbolTint: preset.tint,
    sectionSymbolOpacity: preset.opacity,
    sectionSymbolScale: preset.scale,
    sectionSymbolBlendMode: preset.blendMode,
    ...(preset.renderMode ? { sectionSymbolRenderMode: preset.renderMode } : {}),
    ...(preset.bakedOverlaySrc ? { sectionSymbolBakedOverlaySrc: preset.bakedOverlaySrc } : {}),
  };
};

const resolveSceneHorizonY = (content: Record<string, unknown>) =>
  clamp(
    finiteNumber(content.sectionSceneHorizonY, finiteNumber(content.sectionBacklightY, DEFAULT_SCENE_HORIZON_Y)),
    0,
    120
  );

/** Fraction from top of the section/canvas where the floor zone begins (matches Scene horizon). */
export function resolveSceneHorizonClipFromTop(content: Record<string, unknown>): number {
  return Math.min(1, resolveSceneHorizonY(content) / 100);
}

/** 2D horizon backlight vertical position — anchored to scene horizon when set. */
const resolveSectionBacklightY = (content: Record<string, unknown>) =>
  resolveAnchoredSceneY(content, 'sectionBacklightYOffset', 'sectionBacklightY', 0);

const resolveAnchoredSceneY = (
  content: Record<string, unknown>,
  offsetKey: string,
  legacyAbsoluteKey: string,
  defaultOffset: number
) => {
  const horizonY = resolveSceneHorizonY(content);
  const hasExplicitHorizon = hasFiniteNumber(content.sectionSceneHorizonY);
  const legacyAbsoluteY = finiteNumber(content[legacyAbsoluteKey], horizonY + defaultOffset);
  const fallbackOffset = hasExplicitHorizon ? defaultOffset : legacyAbsoluteY - horizonY;
  return clamp(horizonY + finiteNumber(content[offsetKey], fallbackOffset), 0, 120);
};

const normalizeHex = (value: string) => {
  const trimmed = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
};

const hexToRgb = (value: string) => {
  const normalized = normalizeHex(value);
  if (!normalized) return null;
  const parsed = Number.parseInt(normalized.slice(1), 16);
  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  };
};

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b]
    .map((channel) => Math.round(clamp(channel, 0, 255)).toString(16).padStart(2, '0'))
    .join('')}`;

const rgbToHsl = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const lightness = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: lightness };
  const delta = max - min;
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  const hue =
    max === rn
      ? (gn - bn) / delta + (gn < bn ? 6 : 0)
      : max === gn
        ? (bn - rn) / delta + 2
        : (rn - gn) / delta + 4;
  return { h: hue / 6, s: saturation, l: lightness };
};

const hueToRgb = (p: number, q: number, tValue: number) => {
  let t = tValue;
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
};

const hslToRgb = ({ h, s, l }: { h: number; s: number; l: number }) => {
  if (s === 0) {
    const channel = l * 255;
    return { r: channel, g: channel, b: channel };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hueToRgb(p, q, h + 1 / 3) * 255,
    g: hueToRgb(p, q, h) * 255,
    b: hueToRgb(p, q, h - 1 / 3) * 255,
  };
};

const mixRgb = (
  base: { r: number; g: number; b: number },
  influence: { r: number; g: number; b: number },
  amount: number
) => ({
  r: base.r + (influence.r - base.r) * amount,
  g: base.g + (influence.g - base.g) * amount,
  b: base.b + (influence.b - base.b) * amount,
});

const resolvePremiumGlowColor = (rawColor: string, fallbackColor: string, influence: number) => {
  const rawRgb = hexToRgb(rawColor);
  const fallbackRgb = hexToRgb(fallbackColor) ?? { r: 208, g: 160, b: 79 };
  if (!rawRgb) return rgbToHex(fallbackRgb);
  const hsl = rgbToHsl(rawRgb);
  if (influence >= 0.82) {
    const vividHsl = {
      h: hsl.h,
      s: clamp(hsl.s * 0.78, 0.24, 0.82),
      l: clamp(0.24 + hsl.l * 0.38, 0.2, 0.52),
    };
    return rgbToHex(
      mixRgb(fallbackRgb, hslToRgb(vividHsl), clamp((influence - 0.55) / 0.45, 0, 1))
    );
  }
  const hueDegrees = hsl.h * 360;
  const greenBlueTrim = hueDegrees >= 82 && hueDegrees <= 205 ? 0.72 : 1;
  const yellowTrim = hueDegrees >= 46 && hueDegrees <= 82 ? 0.85 : 1;
  const premiumHsl = {
    h: hsl.h,
    s: clamp(hsl.s * 0.58 * greenBlueTrim * yellowTrim, 0.12, 0.58),
    l: clamp(0.18 + hsl.l * 0.32, 0.16, 0.4),
  };
  return rgbToHex(mixRgb(fallbackRgb, hslToRgb(premiumHsl), clamp(influence, 0, 1)));
};

const blendModeFromValue = (value: unknown): CSSProperties['mixBlendMode'] => {
  const mode = typeof value === 'string' ? value : '';
  if (
    mode === 'normal' ||
    mode === 'screen' ||
    mode === 'overlay' ||
    mode === 'soft-light' ||
    mode === 'multiply' ||
    mode === 'color-dodge'
  ) {
    return mode;
  }
  return 'screen';
};

const symbolMaskStyle = (
  symbolSrc: string,
  symbolScale: number,
  x: number,
  y: number
): Pick<
  CSSProperties,
  | 'maskImage'
  | 'WebkitMaskImage'
  | 'maskRepeat'
  | 'WebkitMaskRepeat'
  | 'maskSize'
  | 'WebkitMaskSize'
  | 'maskPosition'
  | 'WebkitMaskPosition'
> => ({
  maskImage: `url("${safeCssUrl(symbolSrc)}")`,
  WebkitMaskImage: `url("${safeCssUrl(symbolSrc)}")`,
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskSize: `${symbolScale}% auto`,
  WebkitMaskSize: `${symbolScale}% auto`,
  maskPosition: `${x}% ${y}%`,
  WebkitMaskPosition: `${x}% ${y}%`,
});

const offsetByAngle = (angleDeg: number, distance: number) => {
  const radians = (angleDeg * Math.PI) / 180;
  return {
    x: Math.cos(radians) * distance,
    y: Math.sin(radians) * distance,
  };
};

function asSectionSceneContentRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function isSectionBackdropModelViewerBlock(
  block: { type?: string; content?: unknown }
): boolean {
  if (block.type !== 'model-viewer') return false;
  const content = asSectionSceneContentRecord(block.content);
  const immersiveSurface = String(content.surface ?? '').toLowerCase() === 'immersive';
  const publicLightingMode =
    String(content.publicLightingMode ?? 'hero').toLowerCase() === 'product' ? 'product' : 'hero';
  const stageAtmosphere = String(content.stageAtmosphere ?? '').toLowerCase();
  return (
    resolveSectionSceneLayerMode(content, {
      immersiveSurface,
      publicLightingMode,
      stageAtmosphere,
    }) === 'section-backdrop'
  );
}

export function resolveSectionSceneLayerMode(
  content: Record<string, unknown>,
  options: {
    immersiveSurface: boolean;
    publicLightingMode: 'hero' | 'product';
    stageAtmosphere: string;
  }
): SectionSceneLayerMode {
  const rawMode = String(content.sceneLayerMode ?? 'auto').toLowerCase();
  if (rawMode === 'block' || rawMode === 'contained') return 'block';
  if (rawMode === 'section-backdrop' || rawMode === 'full-section') return 'section-backdrop';
  if (
    options.immersiveSurface &&
    options.publicLightingMode === 'hero' &&
    options.stageAtmosphere === 'luxury'
  ) {
    return 'section-backdrop';
  }
  return 'block';
}

export function resolveSectionSceneBacklightStyle(
  content: Record<string, unknown>,
  primaryGlowColor: string,
  secondaryGlowColor: string,
  enabledByDefault: boolean
): CSSProperties | null {
  return resolveSectionSceneBacklightLayers(
    content,
    primaryGlowColor,
    secondaryGlowColor,
    enabledByDefault
  )?.bloomStyle ?? null;
}

function buildWallUplightMask(seamY: number): string {
  const cutStart = clamp(seamY, 0, 100);
  const cutEnd = clamp(seamY + 0.8, cutStart, 100);
  return `linear-gradient(180deg, black 0%, black ${cutStart}%, transparent ${cutEnd}%, transparent 100%)`;
}

function buildFloorBelowMask(floorZoneY: number): string {
  const fadeStart = clamp(floorZoneY, 0, 100);
  const fadeEnd = clamp(floorZoneY + 1.2, fadeStart, 100);
  return `linear-gradient(180deg, transparent 0%, transparent ${fadeStart}%, black ${fadeEnd}%, black 100%)`;
}

/** Keep wall art and uplight on the back wall — never bleed through the transparent floor canvas. */
function buildWallAboveMask(horizonY: number): string {
  const fadeEnd = clamp(horizonY + 1.2, 0, 100);
  return `linear-gradient(180deg, black 0%, black ${horizonY}%, transparent ${fadeEnd}%, transparent 100%)`;
}

function combineCssMasks(...masks: Array<string | undefined>): string {
  return masks.filter((mask): mask is string => Boolean(mask && mask.length > 0)).join(', ');
}

function countCssMaskLayers(mask: string): number {
  const matches = mask.match(/(?:linear|radial|conic)-gradient\(/g);
  return Math.max(1, matches?.length ?? 1);
}

/** Multi-layer CSS masks default to additive compositing — intersect so vignette zones stay dark. */
function applyCssMaskLayers(style: CSSProperties, mask: string): CSSProperties {
  const layerCount = countCssMaskLayers(mask);
  const next: CSSProperties = {
    ...style,
    maskImage: mask,
    WebkitMaskImage: mask,
  };
  if (layerCount > 1) {
    const composite = Array.from({ length: layerCount - 1 }, () => 'source-in').join(', ');
    const intersect = Array.from({ length: layerCount - 1 }, () => 'intersect').join(', ');
    next.WebkitMaskComposite = composite;
    next.maskComposite = intersect;
  }
  return next;
}

type WallLightRevealMaskParams = {
  wallLightX: number;
  wallLightY: number;
  wallLightSpread: number;
  wallLightHeight: number;
  wallLightTextProtect: number;
  /** Shared radial falloff from the wall-light source (texture + symbols). */
  unifiedLightFalloff?: boolean;
};

function buildWallLightTextSideFalloffMask(
  wallLightTextProtect: number,
  softFalloff: boolean
): string {
  const textColumnEdge = clamp(
    softFalloff ? 10 + wallLightTextProtect * 32 : 12 + wallLightTextProtect * 36,
    softFalloff ? 10 : 12,
    softFalloff ? 52 : 56
  );
  if (wallLightTextProtect <= 0.05) {
    return 'linear-gradient(90deg, black 0%, black 100%)';
  }
  if (softFalloff) {
    return `linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) ${Math.round(textColumnEdge - 10)}%, rgba(0,0,0,0.16) ${Math.round(
      textColumnEdge - 2
    )}%, rgba(0,0,0,0.38) ${Math.round(textColumnEdge + 6)}%, rgba(0,0,0,0.62) ${Math.round(
      textColumnEdge + 14
    )}%, rgba(0,0,0,0.82) ${Math.round(textColumnEdge + 22)}%, black ${Math.round(textColumnEdge + 30)}%, black 100%)`;
  }
  return `linear-gradient(90deg, transparent 0%, transparent ${Math.round(textColumnEdge * 0.55)}%, black ${textColumnEdge}%, black 100%)`;
}

function buildWallLightRevealMask({
  wallLightX,
  wallLightY,
  wallLightSpread,
  wallLightHeight,
  wallLightTextProtect,
  unifiedLightFalloff = false,
}: WallLightRevealMaskParams): string {
  const textSideMask = buildWallLightTextSideFalloffMask(wallLightTextProtect, unifiedLightFalloff);

  if (!unifiedLightFalloff) {
    return combineCssMasks(
      `radial-gradient(ellipse ${wallLightSpread * 0.92}% ${wallLightHeight * 1.12}% at ${wallLightX}% ${wallLightY}%, black 0%, black 34%, transparent 78%)`,
      textSideMask
    );
  }

  // Brightest at the wall-light source; smooth falloff with distance (no hard clip pool).
  const lightFalloffMask = `radial-gradient(ellipse ${wallLightSpread * 1.08}% ${wallLightHeight * 1.22}% at ${wallLightX}% ${wallLightY}%, black 0%, black 10%, rgba(0,0,0,0.9) 24%, rgba(0,0,0,0.68) 38%, rgba(0,0,0,0.44) 52%, rgba(0,0,0,0.26) 64%, rgba(0,0,0,0.14) 76%, rgba(0,0,0,0.06) 88%, rgba(0,0,0,0.02) 96%, transparent 100%)`;

  return combineCssMasks(lightFalloffMask, textSideMask);
}

function applyWallAboveClipMask(style: CSSProperties, horizonY: number): CSSProperties {
  const wallAboveMask = buildWallAboveMask(horizonY);
  const existingMask = style.maskImage;
  if (existingMask && typeof existingMask === 'string' && existingMask.length > 0) {
    return {
      ...style,
      maskImage: combineCssMasks(wallAboveMask, existingMask),
      WebkitMaskImage: combineCssMasks(wallAboveMask, existingMask),
    };
  }
  return {
    ...style,
    maskImage: wallAboveMask,
    WebkitMaskImage: wallAboveMask,
  };
}

export function resolveSectionSceneBacklightLayers(
  content: Record<string, unknown>,
  primaryGlowColor: string,
  secondaryGlowColor: string,
  enabledByDefault: boolean
): {
  bloomStyle: CSSProperties;
  coreStyle: CSSProperties;
  floorSheenStyle: CSSProperties;
  wallIlluminationStyle?: CSSProperties;
} | null {
  const enabled =
    content.sectionBacklightEnabled === undefined
      ? enabledByDefault
      : content.sectionBacklightEnabled === true;
  if (!enabled) return null;

  const colourMode = content.sectionBacklightColorMode === 'manual' ? 'manual' : 'colourway';
  const colourInfluence = clamp(finiteNumber(content.sectionBacklightColourInfluence, 0.55), 0, 1);
  const fallbackWarmColor = nonEmptyString(content.sectionBacklightWarmColor, '#d0a04f');
  const fallbackCoolColor = nonEmptyString(content.sectionBacklightCoolColor, '#7b58d6');
  const warmColor =
    colourMode === 'manual'
      ? fallbackWarmColor
      : resolvePremiumGlowColor(primaryGlowColor, '#d0a04f', colourInfluence);
  const coolColor =
    colourMode === 'manual'
      ? fallbackCoolColor
      : resolvePremiumGlowColor(secondaryGlowColor, '#7b58d6', colourInfluence);
  const intensity = clamp(finiteNumber(content.sectionBacklightIntensity, 1.28), 0, 2.5);
  const blur = clamp(finiteNumber(content.sectionBacklightBlur, 24), 0, 90);
  const width = clamp(finiteNumber(content.sectionBacklightWidth, 66), 8, 140);
  const height = clamp(finiteNumber(content.sectionBacklightHeight, 26), 4, 80);
  const x = clamp(finiteNumber(content.sectionBacklightX, 74), 0, 100);
  const seamY = resolveSectionBacklightY(content);
  const floorZoneY = resolveSceneHorizonY(content);
  const coreIntensity = clamp(finiteNumber(content.sectionBacklightCoreIntensity, 0.95), 0, 2);
  const coreBlur = clamp(finiteNumber(content.sectionBacklightCoreBlur, 7), 0, 32);
  const coreWidthControl = clamp(finiteNumber(content.sectionBacklightCoreWidth, 58), 6, 140);
  const coreHeight = clamp(finiteNumber(content.sectionBacklightCoreHeight, 8), 2, 24);
  const floorSheenIntensity = clamp(finiteNumber(content.sectionBacklightFloorSheenIntensity, 0.66), 0, 1.5);
  const floorSheenHeight = clamp(finiteNumber(content.sectionBacklightFloorSheenHeight, 12), 2, 40);
  const edgeFeather = clamp(finiteNumber(content.sectionBacklightEdgeFeather, 18), 0, 42);
  const bloomOpacity = clamp(0.32 + intensity * 0.38, 0, 0.98);
  const coreOpacity = clamp(0.58 + coreIntensity * 0.42, 0, 1);
  const floorSheenOpacity = clamp(0.22 + floorSheenIntensity * 0.42, 0, 0.82);
  const poolOffset = width * 0.045;
  const warmCenterX = clamp(x - poolOffset, 0, 100);
  const coolCenterX = clamp(x + poolOffset, 0, 100);
  const warmBloomWidth = clamp(width * 0.62, 12, 120);
  const coolBloomWidth = clamp(width * 0.58, 12, 120);
  const coreWidth = clamp(coreWidthControl, 8, 140);
  const floorWidth = clamp(width * 0.96, 8, 138);
  const bloomSolidStop = clamp(38 - edgeFeather * 0.34, 12, 38);
  const bloomFadeStop = clamp(66 - edgeFeather * 0.45, bloomSolidStop + 14, 74);
  const coreSolidStop = clamp(28 - edgeFeather * 0.24, 10, 28);
  const coreFadeStop = clamp(58 - edgeFeather * 0.35, coreSolidStop + 12, 64);
  const floorSolidStop = clamp(24 - edgeFeather * 0.22, 8, 24);
  const floorFadeStop = clamp(56 - edgeFeather * 0.28, floorSolidStop + 14, 62);
  const wallUplightMask = buildWallUplightMask(seamY);
  const floorBelowMask = buildFloorBelowMask(floorZoneY);
  const uplightReach = clamp(height * 1.05, 6, 80);
  const uplightCenterY = clamp(seamY - uplightReach * 0.52, 0, 100);
  const seamThickness = clamp(coreHeight * 0.9, 2, 14);
  const floorGlowY = clamp(floorZoneY + floorSheenHeight * 0.2, 0, 100);

  const bloomStyle: CSSProperties = {
    inset: 0,
    opacity: bloomOpacity,
    filter: `blur(${blur}px)`,
    mixBlendMode: 'screen',
    backgroundImage: [
      `radial-gradient(ellipse ${warmBloomWidth}% ${uplightReach}% at ${warmCenterX}% ${uplightCenterY}%, ${warmColor}78 0%, ${warmColor}42 ${bloomSolidStop}%, ${warmColor}10 ${bloomFadeStop}%, transparent 100%)`,
      `radial-gradient(ellipse ${coolBloomWidth}% ${uplightReach}% at ${coolCenterX}% ${uplightCenterY}%, ${coolColor}70 0%, ${coolColor}3e ${bloomSolidStop}%, ${coolColor}10 ${bloomFadeStop}%, transparent 100%)`,
      `radial-gradient(ellipse ${width * 0.68}% ${Math.max(8, uplightReach * 0.65)}% at ${x}% ${uplightCenterY}%, ${warmColor}1c 0%, ${coolColor}12 ${bloomSolidStop + 4}%, transparent ${bloomFadeStop + 6}%)`,
    ].join(', '),
    maskImage: combineCssMasks(wallUplightMask, buildWallAboveMask(floorZoneY)),
    WebkitMaskImage: combineCssMasks(wallUplightMask, buildWallAboveMask(floorZoneY)),
    transform: 'translateZ(0)',
  };

  const coreStyle: CSSProperties = {
    inset: 0,
    opacity: coreOpacity,
    filter: `blur(${Math.max(coreBlur, 4)}px)`,
    mixBlendMode: 'screen',
    backgroundImage: [
      `radial-gradient(ellipse ${coreWidth * 0.48}% ${seamThickness}% at ${warmCenterX}% ${seamY}%, ${warmColor}ee 0%, ${warmColor}66 ${Math.max(12, coreSolidStop)}%, ${warmColor}1a ${coreFadeStop + 6}%, transparent 100%)`,
      `radial-gradient(ellipse ${coreWidth * 0.46}% ${seamThickness}% at ${coolCenterX}% ${seamY}%, ${coolColor}e8 0%, ${coolColor}5c ${Math.max(12, coreSolidStop)}%, ${coolColor}18 ${coreFadeStop + 6}%, transparent 100%)`,
      `radial-gradient(ellipse ${coreWidth * 0.88}% ${Math.max(4, seamThickness * 1.2)}% at ${x}% ${seamY}%, rgba(255,250,242,0.62) 0%, rgba(255,244,235,0.22) ${coreSolidStop + 8}%, transparent ${coreFadeStop + 14}%)`,
    ].join(', '),
    maskImage: wallUplightMask,
    WebkitMaskImage: wallUplightMask,
    transform: 'translateZ(0)',
  };

  const floorSheenStyle: CSSProperties = {
    inset: 0,
    opacity: floorSheenOpacity,
    filter: `blur(${Math.max(8, blur * 0.42)}px)`,
    mixBlendMode: 'screen',
    backgroundImage: [
      `radial-gradient(ellipse ${floorWidth * 0.46}% ${floorSheenHeight}% at ${warmCenterX}% ${floorGlowY}%, ${warmColor}5f 0%, ${warmColor}22 ${floorSolidStop}%, transparent ${floorFadeStop}%)`,
      `radial-gradient(ellipse ${floorWidth * 0.48}% ${floorSheenHeight}% at ${coolCenterX}% ${floorGlowY}%, ${coolColor}62 0%, ${coolColor}24 ${floorSolidStop}%, transparent ${floorFadeStop}%)`,
      `radial-gradient(ellipse ${floorWidth}% ${floorSheenHeight * 0.72}% at ${x}% ${floorGlowY}%, rgba(255,255,255,0.052) 0%, rgba(255,255,255,0.012) ${floorSolidStop + 8}%, transparent ${floorFadeStop}%)`,
    ].join(', '),
    maskImage: floorBelowMask,
    WebkitMaskImage: floorBelowMask,
    transform: 'translateZ(0)',
  };

  const wallIlluminationStyle: CSSProperties = {
    inset: 0,
    opacity: clamp(0.04 + intensity * 0.18, 0, 0.42),
    filter: `blur(${Math.max(blur * 0.75, 12)}px)`,
    mixBlendMode: 'screen',
    backgroundImage: [
      `radial-gradient(ellipse ${width * 0.9}% ${height * 1.4}% at ${x}% ${seamY}%, ${warmColor}40 0%, ${warmColor}18 24%, transparent 56%)`,
      `radial-gradient(ellipse ${width * 0.82}% ${height * 1.3}% at ${x}% ${seamY}%, ${coolColor}30 0%, ${coolColor}10 28%, transparent 58%)`,
    ].join(', '),
    maskImage: buildWallAboveMask(floorZoneY),
    WebkitMaskImage: buildWallAboveMask(floorZoneY),
    transform: 'translateZ(0)',
  };

  return { bloomStyle, coreStyle, floorSheenStyle, wallIlluminationStyle };
}

export type SectionFloorSpillLayer = {
  id: string;
  style: CSSProperties;
};

export type SectionFloorEnvironmentReflectionMode = 'consolidated' | 'legacy';

function applyFloorZoneClipMask(style: CSSProperties, floorFadeStartPercent: number): CSSProperties {
  const fadeStart = clamp(floorFadeStartPercent, 0, 100);
  const fadeEnd = Math.min(100, fadeStart + 5);
  const verticalMask = `linear-gradient(180deg, transparent 0%, transparent ${fadeStart}%, black ${fadeEnd}%, black 100%)`;
  const existingMask = style.maskImage;
  if (existingMask && typeof existingMask === 'string' && existingMask.length > 0) {
    return {
      ...style,
      maskImage: `${verticalMask}, ${existingMask}`,
      WebkitMaskImage: `${verticalMask}, ${existingMask}`,
    };
  }
  return {
    ...style,
    maskImage: verticalMask,
    WebkitMaskImage: verticalMask,
  };
}

function resolveSectionFloorEnvironmentReflectionMode(
  content: Record<string, unknown>
): SectionFloorEnvironmentReflectionMode {
  return content.sectionFloorEnvironmentReflectionMode === 'legacy' ? 'legacy' : 'consolidated';
}

function pushBackgroundLayer(
  target: {
    images: string[];
    blends: string[];
    repeats: string[];
    sizes: string[];
    positions: string[];
  },
  layer: {
    image: string;
    blend: string;
    repeat?: string;
    size?: string;
    position?: string;
  }
) {
  target.images.push(layer.image);
  target.blends.push(layer.blend);
  target.repeats.push(layer.repeat ?? 'no-repeat');
  target.sizes.push(layer.size ?? '100% 100%');
  target.positions.push(layer.position ?? 'center');
}

function resolveConsolidatedSectionFloorEnvironmentReflectionStyle(
  content: Record<string, unknown>,
  options: {
    primaryGlowColor?: string;
    secondaryGlowColor?: string;
    backlightLayers: { floorSheenStyle: CSSProperties } | null;
  }
): CSSProperties | null {
  if (!readSceneLayerToggle(content, 'sectionFloorEnvironmentReflectionEnabled')) {
    return null;
  }

  const sceneHorizonY = resolveSceneHorizonY(content);
  const floorZoneHeight = clamp(100 - sceneHorizonY, 12, 100);
  const floorReflectionEnabled =
    content.sectionFloorArtReflectionEnabled === undefined
      ? true
      : content.sectionFloorArtReflectionEnabled === true;
  const floorReflectionOpacity = clamp(finiteNumber(content.sectionFloorArtReflectionOpacity, 0.32), 0, 1);
  const floorReflectionBlur = clamp(finiteNumber(content.sectionFloorArtReflectionBlur, 7), 0, 48);
  const floorReflectionFade = clamp(finiteNumber(content.sectionFloorArtReflectionFade, 0.9), 0.12, 1);
  const floorReflectionDepth = clamp(finiteNumber(content.sectionFloorArtReflectionDepth, 54), 4, 86);
  const colourInfluence = clamp(finiteNumber(content.sectionBacklightColourInfluence, 0.55), 0, 1);
  const warmColor = resolvePremiumGlowColor(
    options.primaryGlowColor ?? nonEmptyString(content.sectionBacklightWarmColor, '#d0a04f'),
    '#d0a04f',
    colourInfluence
  );
  const coolColor = resolvePremiumGlowColor(
    options.secondaryGlowColor ?? nonEmptyString(content.sectionBacklightCoolColor, '#7b58d6'),
    '#7b58d6',
    colourInfluence
  );
  const backlightX = clamp(finiteNumber(content.sectionBacklightX, 74), 0, 100);
  const backlightWidth = clamp(finiteNumber(content.sectionBacklightWidth, 66), 8, 140);
  const floorSheenEnabled =
    readSceneLayerToggle(content, 'sectionBacklightFloorSheenEnabled') &&
    options.backlightLayers?.floorSheenStyle != null;
  const floorSheenIntensity = floorSheenEnabled
    ? clamp(finiteNumber(content.sectionBacklightFloorSheenIntensity, 0.66), 0, 1.5)
    : 0;
  const floorSheenHeight = clamp(finiteNumber(content.sectionBacklightFloorSheenHeight, 12), 2, 40);
  const warmCenterX = clamp(backlightX - backlightWidth * 0.035, 0, 100);
  const coolCenterX = clamp(backlightX + backlightWidth * 0.045, 0, 100);

  const presetId = resolveWallTexturePresetId(content);
  const preset = findWallTexturePreset(presetId);
  const usePreset = presetId !== CUSTOM_WALL_TEXTURE_PRESET_ID;
  const textureRepeatMode = resolveWallTextureRepeatMode(content, preset, usePreset);
  const textureScale = clamp(
    finiteNumber(content.sectionWallTextureScale, usePreset ? preset.scale : finiteNumber(content.sectionArtScale, 118)),
    20,
    260
  );
  const textureOpacity = clamp(
    finiteNumber(content.sectionWallTextureOpacity, usePreset ? preset.opacity : 0.08),
    0,
    1
  );
  const textureBrightness = clamp(
    finiteNumber(content.sectionWallTextureBrightness, usePreset ? preset.brightness : 0.78),
    0.35,
    1.8
  );
  const textureContrast = clamp(
    finiteNumber(content.sectionWallTextureContrast, usePreset ? preset.contrast : 1.12),
    0.5,
    2.2
  );
  const wallTextureEnabled =
    content.sectionArtEnabled === undefined ? true : content.sectionArtEnabled === true;
  const textureSrc =
    floorReflectionEnabled && wallTextureEnabled
      ? usePreset
        ? preset.textureSrc
        : resolveWallTextureSrc(content.sectionWallTextureSrc)
      : '';
  const tint = nonEmptyString(content.sectionArtTint, usePreset ? preset.tint : '#9b6f35');
  const textureX = clamp(finiteNumber(content.sectionArtX, 58), 0, 100);
  const textureY = clamp(finiteNumber(content.sectionArtY, 42), 0, 100);

  const symbolFamilyPresetId = resolveSymbolFamilyPresetId(content);
  const symbolFamilyPreset = findSymbolFamilyPreset(symbolFamilyPresetId);
  const useSymbolFamilyPreset = symbolFamilyPresetId !== CUSTOM_SYMBOL_FAMILY_PRESET_ID;
  const symbolRenderMode = resolveSymbolRenderMode(content.sectionSymbolRenderMode);
  const symbolsEnabled = content.legacySceneSymbolsEnabled === true && floorReflectionEnabled;
  const symbolScale = clamp(
    finiteNumber(content.sectionSymbolScale, useSymbolFamilyPreset ? symbolFamilyPreset.scale : 118),
    20,
    260
  );
  const symbolX = clamp(finiteNumber(content.sectionSymbolX, textureX), 0, 100);
  const symbolY = clamp(finiteNumber(content.sectionSymbolY, textureY), 0, 100);
  const symbolSrc = symbolsEnabled
    ? symbolRenderMode === 'baked-overlay'
      ? nonEmptyString(
          content.sectionSymbolBakedOverlaySrc,
          useSymbolFamilyPreset && symbolFamilyPreset.bakedOverlaySrc
            ? symbolFamilyPreset.bakedOverlaySrc
            : DEFAULT_BAKED_SYMBOL_OVERLAY_SRC
        )
      : useSymbolFamilyPreset
        ? symbolFamilyPreset.symbolSrc
        : nonEmptyString(content.sectionSymbolOverlaySrc, DEFAULT_SYMBOL_OVERLAY_SRC)
    : '';

  const backgrounds = {
    images: [] as string[],
    blends: [] as string[],
    repeats: [] as string[],
    sizes: [] as string[],
    positions: [] as string[],
  };

  if (floorSheenIntensity > 0) {
    const floorGlowDepth = clamp(18 + floorSheenHeight * 1.55, 18, 74);
    pushBackgroundLayer(backgrounds, {
      image: `radial-gradient(ellipse ${backlightWidth * 0.48}% ${floorGlowDepth}% at ${warmCenterX}% 0%, ${warmColor}70 0%, ${warmColor}34 24%, ${warmColor}0e 58%, transparent 100%)`,
      blend: 'screen',
    });
    pushBackgroundLayer(backgrounds, {
      image: `radial-gradient(ellipse ${backlightWidth * 0.52}% ${floorGlowDepth}% at ${coolCenterX}% 0%, ${coolColor}6c 0%, ${coolColor}30 26%, ${coolColor}0e 60%, transparent 100%)`,
      blend: 'screen',
    });
    pushBackgroundLayer(backgrounds, {
      image: `radial-gradient(ellipse ${backlightWidth * 0.94}% ${Math.max(14, floorGlowDepth * 0.46)}% at ${backlightX}% 0%, rgba(255,244,220,0.28) 0%, rgba(255,244,220,0.06) 38%, transparent 78%)`,
      blend: 'screen',
    });
  }

  if (textureSrc) {
    const fullSectionTextureHeight = clamp(10000 / floorZoneHeight, 180, 760);
    const floorTextureY = clamp(100 - textureY + floorReflectionDepth * 0.08, 42, 100);
    const texturePickupStrength = clamp(
      0.12 + textureOpacity * 0.58 + floorReflectionOpacity * 0.72 + floorSheenIntensity * 0.08,
      0,
      1
    );
    pushBackgroundLayer(backgrounds, {
      image: `linear-gradient(180deg, ${colorWithAlpha(tint, 0.06 + texturePickupStrength * 0.18)} 0%, ${colorWithAlpha(tint, 0.025 + texturePickupStrength * 0.1)} 44%, transparent 100%)`,
      blend: 'soft-light',
    });
    pushBackgroundLayer(backgrounds, {
      image: `url("${safeCssUrl(textureSrc)}")`,
      blend: 'soft-light',
      repeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
      size:
        textureRepeatMode === 'repeat'
          ? `${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
          : 'cover',
      position: `${textureX}% ${100 - textureY}%`,
    });
    pushBackgroundLayer(backgrounds, {
      image: `linear-gradient(180deg, ${colorWithAlpha(tint, 0.1 + texturePickupStrength * 0.2)} 0%, ${colorWithAlpha(tint, 0.045 + texturePickupStrength * 0.12)} 28%, ${colorWithAlpha(tint, 0.018 + texturePickupStrength * 0.055)} 58%, transparent 100%)`,
      blend: 'overlay',
    });
    pushBackgroundLayer(backgrounds, {
      image: `url("${safeCssUrl(textureSrc)}")`,
      blend: 'overlay',
      repeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
      size:
        textureRepeatMode === 'repeat'
          ? `${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
          : `100% ${fullSectionTextureHeight}%`,
      position: `${textureX}% ${floorTextureY}%`,
    });
  }

  if (symbolSrc) {
    pushBackgroundLayer(backgrounds, {
      image: `url("${safeCssUrl(symbolSrc)}")`,
      blend: 'soft-light',
      repeat: 'no-repeat',
      size: `${symbolScale}% auto`,
      position: `${symbolX}% ${clamp(100 - symbolY + floorReflectionDepth * 0.08, 48, 100)}%`,
    });
  }

  if (backgrounds.images.length === 0) return null;

  const lightGateWidth = clamp(backlightWidth * 0.92 + floorReflectionDepth * 0.34, 24, 104);
  const lightGateDepth = clamp(36 + floorReflectionDepth * 1.04 + floorSheenHeight * 0.9, 42, 140);
  const reflectionMask = `radial-gradient(ellipse ${lightGateWidth}% ${lightGateDepth}% at ${backlightX}% 0%, rgba(0,0,0,0.94) 0%, rgba(0,0,0,${0.68 * floorReflectionFade}) 24%, rgba(0,0,0,${0.28 * floorReflectionFade}) 52%, rgba(0,0,0,0.08) 72%, transparent 100%)`;
  const floorTextureOpacityBoost = textureSrc ? textureOpacity * 0.08 : 0;
  const floorTextureBlurCut = textureSrc ? textureOpacity * 1.3 : 0;

  return {
    position: 'absolute',
    left: 0,
    right: 0,
    top: `${sceneHorizonY}%`,
    height: `${floorZoneHeight}%`,
    opacity: clamp(0.1 + floorReflectionOpacity * 1.12 + floorSheenIntensity * 0.12 + floorTextureOpacityBoost, 0, 0.9),
    backgroundImage: backgrounds.images.join(', '),
    backgroundBlendMode: backgrounds.blends.join(', '),
    backgroundRepeat: backgrounds.repeats.join(', '),
    backgroundSize: backgrounds.sizes.join(', '),
    backgroundPosition: backgrounds.positions.join(', '),
    mixBlendMode: 'screen',
    filter: `blur(${clamp(1.4 + floorReflectionBlur * 0.24 - floorTextureBlurCut, 1, 16)}px) brightness(${clamp(0.82 + textureBrightness * 0.14 + floorSheenIntensity * 0.035, 0.82, 1.16)}) contrast(${clamp(1.02 + textureContrast * 0.16 + textureOpacity * 0.08, 1.08, 1.46)})`,
    maskImage: reflectionMask,
    WebkitMaskImage: reflectionMask,
    pointerEvents: 'none',
    transform: 'translateZ(0)',
  };
}

/** Dark stage floor fill from the wall/floor divide to the viewport bottom. */
export function resolveSectionSceneFloorBaseFillStyle(
  content: Record<string, unknown>
): CSSProperties | null {
  if (isSectionBackdropQuarantined('cssFloorSpillLayers')) return null;

  const horizonY = resolveSceneHorizonY(content);
  const floorDefaults =
    content.sectionFloorPipelineDefaults &&
    typeof content.sectionFloorPipelineDefaults === 'object' &&
    !Array.isArray(content.sectionFloorPipelineDefaults)
      ? (content.sectionFloorPipelineDefaults as Record<string, unknown>)
      : {};
  const floorColor = nonEmptyString(floorDefaults.floorColor, '#121010');
  const floorTop = clamp(horizonY, 0, 96);
  const floorHeightPercent = clamp(100 - floorTop, 20, 100);
  const blendStop = 14;

  return {
    position: 'absolute',
    left: 0,
    right: 0,
    top: `${floorTop}%`,
    height: `${floorHeightPercent}%`,
    backgroundImage: [
      `linear-gradient(180deg, transparent 0%, ${floorColor}28 ${blendStop - 10}%, ${floorColor}c8 ${blendStop + 4}%, ${floorColor}e0 ${blendStop + 18}%, #0a0808 72%, #080606 100%)`,
      `radial-gradient(ellipse 140% 100% at 50% 0%, rgba(255,255,255,0.06) 0%, transparent 62%)`,
    ].join(', '),
    pointerEvents: 'none',
    transform: 'translateZ(0)',
  };
}

/** Full-width horizontal glow band at the scene horizon — keeps floor spill level across the stage. */
export function resolveSectionHorizonFloorBandStyle(
  content: Record<string, unknown>,
  primaryGlowColor: string,
  secondaryGlowColor: string,
  enabledByDefault: boolean
): CSSProperties | null {
  const enabled =
    content.sectionBacklightEnabled === undefined
      ? enabledByDefault
      : content.sectionBacklightEnabled === true;
  if (!enabled) return null;
  if (isSectionBackdropQuarantined('cssFloorSpillLayers')) return null;
  if (!readSceneLayerToggle(content, 'sectionFloorSpillHorizonBandEnabled')) return null;

  const colourMode = content.sectionBacklightColorMode === 'manual' ? 'manual' : 'colourway';
  const colourInfluence = clamp(finiteNumber(content.sectionBacklightColourInfluence, 0.55), 0, 1);
  const fallbackWarmColor = nonEmptyString(content.sectionBacklightWarmColor, '#d0a04f');
  const fallbackCoolColor = nonEmptyString(content.sectionBacklightCoolColor, '#7b58d6');
  const warmColor =
    colourMode === 'manual'
      ? fallbackWarmColor
      : resolvePremiumGlowColor(primaryGlowColor, '#d0a04f', colourInfluence);
  const coolColor =
    colourMode === 'manual'
      ? fallbackCoolColor
      : resolvePremiumGlowColor(secondaryGlowColor, '#7b58d6', colourInfluence);
  const intensity = clamp(finiteNumber(content.sectionBacklightIntensity, 1.28), 0, 2.5);
  const blur = clamp(finiteNumber(content.sectionBacklightBlur, 24), 0, 90);
  const sceneHorizonY = resolveSceneHorizonY(content);
  const floorDepth = clamp(finiteNumber(content.sectionFloorArtReflectionDepth, 54), 8, 86);
  const floorBandHeight = clamp(100 - sceneHorizonY, 16, 100);
  const floorSheenIntensity = clamp(
    finiteNumber(content.sectionBacklightFloorSheenIntensity, 0.66),
    0,
    1.5
  );

  return {
    left: 0,
    right: 0,
    top: `${sceneHorizonY}%`,
    height: `${floorBandHeight}%`,
    mixBlendMode: 'screen',
    opacity: clamp(0.22 + intensity * 0.26 + floorSheenIntensity * 0.18, 0, 0.72),
    filter: `blur(${Math.max(10, blur * 0.5)}px)`,
    backgroundImage: [
      `linear-gradient(180deg, ${warmColor}62 0%, ${warmColor}24 16%, ${warmColor}08 42%, transparent 100%)`,
      `linear-gradient(180deg, ${coolColor}58 0%, ${coolColor}20 18%, ${coolColor}08 44%, transparent 100%)`,
      'linear-gradient(180deg, rgba(255,236,210,0.05) 0%, rgba(255,236,210,0.012) 36%, transparent 100%)',
    ].join(', '),
    maskImage:
      'linear-gradient(180deg, black 0%, black 14%, rgba(0,0,0,0.78) 48%, rgba(0,0,0,0.28) 78%, transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(180deg, black 0%, black 14%, rgba(0,0,0,0.78) 48%, rgba(0,0,0,0.28) 78%, transparent 100%)',
    transform: 'translateZ(0)',
  };
}

/** Soft colourway-tinted haze at the vase foot — blends model base into the floor zone. */
export function resolveSectionSceneBaseMistStyle(
  content: Record<string, unknown>,
  primaryGlowColor: string,
  secondaryGlowColor: string,
  enabledByDefault = true
): CSSProperties | null {
  const enabled =
    content.sectionSceneBaseMistEnabled === undefined
      ? enabledByDefault
      : content.sectionSceneBaseMistEnabled === true;
  if (!enabled) return null;

  const intensity = clamp(finiteNumber(content.sectionSceneBaseMistIntensity, 0.42), 0, 1);
  const y = resolveSectionBacklightY(content);
  const mistX = clamp(finiteNumber(content.sectionBacklightX, 74), 0, 100);
  const warmColor = resolvePremiumGlowColor(primaryGlowColor, '#d0a04f', 0.58);
  const coolColor = resolvePremiumGlowColor(secondaryGlowColor, '#7b58d6', 0.58);
  const mistTop = clamp(y - 6, 0, 100);
  const mistMid = clamp(y + 2, 0, 100);
  const mistBottom = clamp(y + 20, 0, 100);
  const mistOffset = 5;

  return {
    inset: 0,
    opacity: clamp(0.18 + intensity * 0.42, 0, 0.72),
    filter: `blur(${10 + intensity * 12}px)`,
    mixBlendMode: 'screen',
    backgroundImage: [
      `radial-gradient(ellipse 38% 20% at ${clamp(mistX - mistOffset, 0, 100)}% ${mistMid}%, ${warmColor}52 0%, ${warmColor}18 42%, transparent 78%)`,
      `radial-gradient(ellipse 36% 18% at ${clamp(mistX + mistOffset, 0, 100)}% ${mistMid}%, ${coolColor}48 0%, ${coolColor}16 40%, transparent 76%)`,
      `radial-gradient(ellipse 44% 22% at ${mistX}% ${mistMid}%, rgba(255,248,240,0.14) 0%, transparent 72%)`,
      `linear-gradient(180deg, transparent ${mistTop}%, rgba(0,0,0,0.22) ${mistMid}%, transparent ${mistBottom}%)`,
    ].join(', '),
    maskImage: `linear-gradient(180deg, transparent 0%, black ${mistTop}%, black ${mistBottom}%, transparent 100%)`,
    WebkitMaskImage: `linear-gradient(180deg, transparent 0%, black ${mistTop}%, black ${mistBottom}%, transparent 100%)`,
    transform: 'translateZ(0)',
  };
}

/** Soft colourway reflection tail — extends readable spill to the viewport bottom below the 3D alpha fade. */
export function resolveSectionColourwayFloorTailStyle(
  content: Record<string, unknown>,
  primaryGlowColor: string,
  secondaryGlowColor: string
): CSSProperties | null {
  if (isSectionBackdropQuarantined('cssFloorSpillLayers')) return null;
  if (!readSceneLayerToggle(content, 'sectionColourwayFloorTailEnabled')) return null;

  const horizonY = resolveSceneHorizonY(content);
  const colourInfluence = clamp(finiteNumber(content.sectionBacklightColourInfluence, 0.88), 0, 1);
  const warmColor = resolvePremiumGlowColor(primaryGlowColor, '#d0a04f', colourInfluence);
  const coolColor = resolvePremiumGlowColor(secondaryGlowColor, '#7b58d6', colourInfluence);
  const floorHeight = clamp(100 - horizonY, 24, 100);
  const tailX = clamp(finiteNumber(content.sectionBacklightX, 74), 0, 100);

  return {
    position: 'absolute',
    left: '6%',
    right: '6%',
    top: `${clamp(horizonY + 1, 0, 96)}%`,
    height: `${floorHeight - 1}%`,
    opacity: 0.48,
    filter: 'blur(24px)',
    mixBlendMode: 'screen',
    backgroundImage: [
      `linear-gradient(180deg, ${warmColor}30 0%, ${warmColor}14 24%, transparent 52%)`,
      `linear-gradient(180deg, ${coolColor}28 0%, ${coolColor}12 26%, transparent 54%)`,
      `radial-gradient(ellipse 42% 92% at ${tailX}% 0%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 38%, transparent 72%)`,
    ].join(', '),
    maskImage:
      'linear-gradient(180deg, black 0%, black 48%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.18) 92%, transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(180deg, black 0%, black 48%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.18) 92%, transparent 100%)',
    pointerEvents: 'none',
    transform: 'translateZ(0)',
  };
}

/** Scene spill layers composited above the 3D canvas so wall art, symbols, and horizon glow reach the floor. */
export function resolveSectionSceneFloorSpillLayers(
  content: Record<string, unknown>,
  options: {
    primaryGlowColor?: string;
    secondaryGlowColor?: string;
    sceneArt: {
      floorReflectionStyle: CSSProperties | null;
      symbolFloorReflectionStyle: CSSProperties | null;
    } | null;
    backlightLayers: { floorSheenStyle: CSSProperties } | null;
    wallLightStyle: CSSProperties | null;
  }
): SectionFloorSpillLayer[] {
  const sceneHorizonY = resolveSceneHorizonY(content);
  const layers: SectionFloorSpillLayer[] = [];
  const environmentReflectionMode = resolveSectionFloorEnvironmentReflectionMode(content);

  if (environmentReflectionMode === 'consolidated') {
    const consolidatedStyle = resolveConsolidatedSectionFloorEnvironmentReflectionStyle(content, {
      primaryGlowColor: options.primaryGlowColor,
      secondaryGlowColor: options.secondaryGlowColor,
      backlightLayers: options.backlightLayers,
    });
    return consolidatedStyle
      ? [
          {
            id: 'environment-floor-reflection',
            style: consolidatedStyle,
          },
        ]
      : [];
  }

  const horizonBandStyle =
    options.primaryGlowColor && options.secondaryGlowColor
      ? resolveSectionHorizonFloorBandStyle(
          content,
          options.primaryGlowColor,
          options.secondaryGlowColor,
          true
        )
      : null;
  if (horizonBandStyle) {
    layers.push({
      id: 'horizon-floor-band',
      style: {
        position: 'absolute',
        ...horizonBandStyle,
      },
    });
  }

  if (options.primaryGlowColor && options.secondaryGlowColor) {
    const colourwayFloorTailStyle = resolveSectionColourwayFloorTailStyle(
      content,
      options.primaryGlowColor,
      options.secondaryGlowColor
    );
    if (colourwayFloorTailStyle) {
      layers.push({
        id: 'colourway-floor-tail',
        style: colourwayFloorTailStyle,
      });
    }
  }

  if (options.sceneArt?.floorReflectionStyle) {
    const reflectionOpacity =
      typeof options.sceneArt.floorReflectionStyle.opacity === 'number'
        ? options.sceneArt.floorReflectionStyle.opacity
        : 1;
    layers.push({
      id: 'wall-floor-reflection',
      style: applyFloorZoneClipMask(
        {
          position: 'absolute',
          ...options.sceneArt.floorReflectionStyle,
          opacity: clamp(reflectionOpacity * 1.18, 0, 0.42),
        },
        sceneHorizonY
      ),
    });
  }

  if (options.sceneArt?.symbolFloorReflectionStyle) {
    const symbolOpacity =
      typeof options.sceneArt.symbolFloorReflectionStyle.opacity === 'number'
        ? options.sceneArt.symbolFloorReflectionStyle.opacity
        : 1;
    layers.push({
      id: 'symbol-floor-reflection',
      style: applyFloorZoneClipMask(
        {
          position: 'absolute',
          ...options.sceneArt.symbolFloorReflectionStyle,
          opacity: clamp(symbolOpacity * 1.18, 0, 0.44),
        },
        sceneHorizonY
      ),
    });
  }

  if (options.backlightLayers?.floorSheenStyle) {
    const sheenEnabled = readSceneLayerToggle(content, 'sectionBacklightFloorSheenEnabled');
    if (sheenEnabled) {
      const sheenOpacity =
        typeof options.backlightLayers.floorSheenStyle.opacity === 'number'
          ? options.backlightLayers.floorSheenStyle.opacity
          : 1;
      layers.push({
        id: 'horizon-floor-sheen',
        style: {
          position: 'absolute',
          ...options.backlightLayers.floorSheenStyle,
          opacity: clamp(sheenOpacity * 1.12, 0, 0.78),
        },
      });
    }
  }

  if (
    !isSectionBackdropQuarantined('cssFloorSpillLayers') &&
    options.wallLightStyle &&
    readSceneLayerToggle(content, 'sectionWallLightFloorSpillEnabled')
  ) {
    const baseOpacity =
      typeof options.wallLightStyle.opacity === 'number' ? options.wallLightStyle.opacity : 1;
    layers.push({
      id: 'wall-light-floor-spill',
      style: applyFloorZoneClipMask(
        {
          position: 'absolute',
          inset: 0,
          ...options.wallLightStyle,
          opacity: clamp(baseOpacity * 0.58, 0, 0.48),
        },
        sceneHorizonY
      ),
    });
  }

  return layers;
}

export function resolveSectionWallLightRevealStyle(
  content: Record<string, unknown>,
  primaryGlowColor: string,
  secondaryGlowColor: string,
  enabledByDefault: boolean
): CSSProperties | null {
  const enabled =
    content.sectionWallLightEnabled === undefined
      ? enabledByDefault
      : content.sectionWallLightEnabled === true;
  if (!enabled) return null;

  const colourMode = content.sectionWallLightColorMode === 'manual' ? 'manual' : 'colourway';
  const colourInfluence = clamp(
    finiteNumber(content.sectionWallLightColourInfluence, finiteNumber(content.sectionBacklightColourInfluence, 0.48)),
    0,
    1
  );
  const fallbackWarmColor = nonEmptyString(
    content.sectionWallLightWarmColor,
    nonEmptyString(content.sectionBacklightWarmColor, '#d0a04f')
  );
  const fallbackCoolColor = nonEmptyString(
    content.sectionWallLightCoolColor,
    nonEmptyString(content.sectionBacklightCoolColor, '#7b58d6')
  );
  const warmColor =
    colourMode === 'manual'
      ? fallbackWarmColor
      : resolvePremiumGlowColor(primaryGlowColor, '#d0a04f', colourInfluence);
  const coolColor =
    colourMode === 'manual'
      ? fallbackCoolColor
      : resolvePremiumGlowColor(secondaryGlowColor, '#7b58d6', colourInfluence);
  const intensity = clamp(finiteNumber(content.sectionWallLightIntensity, 0.7), 0, 2);
  const spread = clamp(finiteNumber(content.sectionWallLightSpread, 72), 12, 150);
  const height = clamp(finiteNumber(content.sectionWallLightHeight, 54), 8, 120);
  const softness = clamp(finiteNumber(content.sectionWallLightSoftness, 28), 0, 90);
  const x = clamp(finiteNumber(content.sectionWallLightX, finiteNumber(content.sectionBacklightX, 74)), 0, 100);
  const y = resolveAnchoredSceneY(content, 'sectionWallLightYOffset', 'sectionWallLightY', -24);
  const textProtect = clamp(finiteNumber(content.sectionWallLightTextProtect, 0.72), 0, 1);
  const warmX = clamp(x - spread * 0.16, -18, 118);
  const coolX = clamp(x + spread * 0.2, -18, 118);
  const warmY = clamp(y + height * 0.08, 0, 100);
  const coolY = clamp(y + height * 0.02, 0, 100);
  const opacity = clamp(0.08 + intensity * 0.24, 0, 0.62);
  const leftMask = clamp(0.12 + (1 - textProtect) * 0.58, 0.12, 0.7);
  const revealStop = clamp(18 + textProtect * 24, 18, 42);

  const sceneHorizonY = resolveSceneHorizonY(content);
  return applyWallAboveClipMask(
    {
      inset: 0,
      opacity,
      filter: `blur(${softness}px)`,
      mixBlendMode: 'screen',
      backgroundImage: [
        `radial-gradient(ellipse ${spread * 0.56}% ${height}% at ${warmX}% ${warmY}%, ${warmColor}42 0%, ${warmColor}20 34%, ${warmColor}08 62%, transparent 100%)`,
        `radial-gradient(ellipse ${spread * 0.52}% ${height * 0.92}% at ${coolX}% ${coolY}%, ${coolColor}38 0%, ${coolColor}1d 34%, ${coolColor}08 62%, transparent 100%)`,
        `radial-gradient(ellipse ${spread}% ${height * 0.76}% at ${x}% ${clamp(y + height * 0.16, 0, 100)}%, rgba(255,226,168,0.08) 0%, rgba(255,226,168,0.026) 38%, transparent 78%)`,
      ].join(', '),
      maskImage: `linear-gradient(90deg, rgba(0,0,0,${leftMask}) 0%, rgba(0,0,0,${Math.max(
        leftMask,
        0.28
      )}) ${Math.max(8, revealStop - 16)}%, black ${revealStop}%, black 100%)`,
      WebkitMaskImage: `linear-gradient(90deg, rgba(0,0,0,${leftMask}) 0%, rgba(0,0,0,${Math.max(
        leftMask,
        0.28
      )}) ${Math.max(8, revealStop - 16)}%, black ${revealStop}%, black 100%)`,
      transform: 'translateZ(0)',
    },
    sceneHorizonY
  );
}

export function resolveSectionForegroundReflectionVeilStyle(
  content: Record<string, unknown>,
  enabledByDefault = true
): CSSProperties | null {
  const enabled =
    content.sectionReflectionForegroundVeilEnabled === undefined
      ? enabledByDefault
      : content.sectionReflectionForegroundVeilEnabled === true;
  const opacity = clamp(finiteNumber(content.sectionReflectionForegroundVeilOpacity, 0.16), 0, 0.6);
  if (!enabled || opacity <= 0) return null;

  const sceneHorizonY = resolveSceneHorizonY(content);
  const wallLightX = clamp(finiteNumber(content.sectionWallLightX, finiteNumber(content.sectionBacklightX, 74)), 0, 100);
  const blur = clamp(finiteNumber(content.sectionReflectionForegroundVeilBlur, 16), 0, 36);
  const depth = clamp(finiteNumber(content.sectionReflectionForegroundVeilDepth, 34), 4, 80);
  const start = clamp(finiteNumber(content.sectionReflectionForegroundVeilStart, 0), 0, 44);
  const softness = clamp(finiteNumber(content.sectionReflectionForegroundVeilSoftness, 0.72), 0.12, 1);
  const top = clamp(sceneHorizonY + start, 0, 120);
  const left = clamp(wallLightX - 42, 28, 58);
  const localLightX = clamp(((wallLightX - left) / Math.max(1, 100 - left)) * 100, 34, 88);
  const fadeIn = Math.round(12 + softness * 24);
  const hold = Math.round(72 + softness * 12);
  const verticalMask = `linear-gradient(180deg, transparent 0%, black ${fadeIn}%, black ${hold}%, transparent 100%)`;

  return {
    left: `${left}%`,
    right: 0,
    top: `${top}%`,
    height: `${depth}%`,
    opacity,
    backgroundImage: [
      `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.16) ${Math.round(
        18 + softness * 24
      )}%, rgba(0,0,0,0.76) 100%)`,
      `radial-gradient(ellipse 82% 118% at ${localLightX}% 12%, rgba(255,255,255,0.026) 0%, transparent 62%)`,
    ].join(', '),
    filter: `blur(${blur}px)`,
    clipPath: 'inset(0)',
    maskImage: verticalMask,
    WebkitMaskImage: verticalMask,
    transform: 'translateZ(0)',
    willChange: 'opacity, filter, transform',
  };
}

export function resolveSectionSceneArt(
  content: Record<string, unknown>,
  enabledByDefault: boolean,
  primaryGlowColor = '#c8a45d',
  secondaryGlowColor = '#7c5fd6'
): {
  wallWrapperStyle: CSSProperties;
  symbolWrapperStyle: CSSProperties;
  textureStyle: CSSProperties | null;
  textureDepthShadowStyle: CSSProperties | null;
  textureDepthHighlightStyle: CSSProperties | null;
  textureLightRevealStyle: CSSProperties | null;
  floorReflectionStyle: CSSProperties | null;
  symbolFloorReflectionStyle: CSSProperties | null;
  symbolAuraStyle: CSSProperties | null;
  symbolEffectLayerStyles: CSSProperties[];
  symbolBakedOverlayStyle: CSSProperties | null;
  symbolStyle: CSSProperties | null;
} | null {
  const wallTextureEnabled =
    content.sectionArtEnabled === undefined ? enabledByDefault : content.sectionArtEnabled === true;
  const legacySceneSymbolsEnabled = content.legacySceneSymbolsEnabled === true;
  if (!wallTextureEnabled && !legacySceneSymbolsEnabled) return null;

  const presetId = resolveWallTexturePresetId(content);
  const preset = findWallTexturePreset(presetId);
  const usePreset = presetId !== CUSTOM_WALL_TEXTURE_PRESET_ID;
  const symbolFamilyPresetId = resolveSymbolFamilyPresetId(content);
  const symbolFamilyPreset = findSymbolFamilyPreset(symbolFamilyPresetId);
  const useSymbolFamilyPreset = symbolFamilyPresetId !== CUSTOM_SYMBOL_FAMILY_PRESET_ID;
  const symbolRenderMode = resolveSymbolRenderMode(content.sectionSymbolRenderMode);
  const vectorSymbolsEnabled = legacySceneSymbolsEnabled && symbolRenderMode === 'vector-mask';
  const bakedSymbolsEnabled = legacySceneSymbolsEnabled && symbolRenderMode === 'baked-overlay';
  const symbolEffectPreset = resolveSymbolEffectPresetId(content.sectionSymbolEffectPreset ?? 'raised-gilded');
  const tint = nonEmptyString(content.sectionArtTint, usePreset ? preset.tint : '#9b6f35');
  const blendMode = blendModeFromValue(content.sectionArtBlendMode ?? (usePreset ? preset.blendMode : 'soft-light'));
  const symbolTint = nonEmptyString(content.sectionSymbolTint, useSymbolFamilyPreset ? symbolFamilyPreset.tint : tint);
  const symbolBlendMode = blendModeFromValue(
    content.sectionSymbolBlendMode ??
      (useSymbolFamilyPreset ? symbolFamilyPreset.blendMode : content.sectionArtBlendMode) ??
      'soft-light'
  );
  const scale = clamp(finiteNumber(content.sectionArtScale, 118), 20, 260);
  const textureOpacity = clamp(
    finiteNumber(content.sectionWallTextureOpacity, usePreset ? preset.opacity : 0.08),
    0,
    1
  );
  const symbolOpacity = clamp(
    finiteNumber(content.sectionSymbolOpacity, useSymbolFamilyPreset ? symbolFamilyPreset.opacity : 0.36),
    0,
    1
  );
  const textureScale = clamp(
    finiteNumber(content.sectionWallTextureScale, usePreset ? preset.scale : scale),
    20,
    260
  );
  const symbolScale = clamp(
    finiteNumber(content.sectionSymbolScale, useSymbolFamilyPreset ? symbolFamilyPreset.scale : scale),
    20,
    260
  );
  const textureBrightness = clamp(
    finiteNumber(content.sectionWallTextureBrightness, usePreset ? preset.brightness : 0.78),
    0.35,
    1.8
  );
  const textureContrast = clamp(
    finiteNumber(content.sectionWallTextureContrast, usePreset ? preset.contrast : 1.12),
    0.5,
    2.2
  );
  const textureRepeatMode = resolveWallTextureRepeatMode(content, preset, usePreset);
  const x = clamp(finiteNumber(content.sectionArtX, 58), 0, 100);
  const y = clamp(finiteNumber(content.sectionArtY, 42), 0, 100);
  const vignette = clamp(finiteNumber(content.sectionArtVignette, 0.72), 0.2, 1);
  const symbolX = clamp(finiteNumber(content.sectionSymbolX, x), 0, 100);
  const symbolY = clamp(finiteNumber(content.sectionSymbolY, y), 0, 100);
  const symbolVignette = clamp(finiteNumber(content.sectionSymbolVignette, vignette), 0.2, 1);
  const textureSrc = wallTextureEnabled
    ? usePreset
      ? preset.textureSrc
      : resolveWallTextureSrc(content.sectionWallTextureSrc)
    : '';
  const symbolSrc = vectorSymbolsEnabled
    ? useSymbolFamilyPreset
      ? symbolFamilyPreset.symbolSrc
      : nonEmptyString(content.sectionSymbolOverlaySrc, DEFAULT_SYMBOL_OVERLAY_SRC)
    : '';
  const bakedSymbolSrc = bakedSymbolsEnabled
    ? nonEmptyString(
        content.sectionSymbolBakedOverlaySrc,
        useSymbolFamilyPreset && symbolFamilyPreset.bakedOverlaySrc
          ? symbolFamilyPreset.bakedOverlaySrc
          : DEFAULT_BAKED_SYMBOL_OVERLAY_SRC
      )
    : '';
  const symbolEffectStrength = clamp(finiteNumber(content.sectionSymbolEffectStrength, 0.42), 0, 1);
  const symbolEffectDistance = clamp(finiteNumber(content.sectionSymbolEffectDistance, 0.9), 0, 8);
  const symbolEffectSoftness = clamp(finiteNumber(content.sectionSymbolEffectSoftness, 1.8), 0, 16);
  const symbolLineWeight = clamp(finiteNumber(content.sectionSymbolLineWeight, 1.2), 0.5, 3);
  const symbolEffectHighlightColor = nonEmptyString(content.sectionSymbolEffectHighlightColor, '#f1d69a');
  const symbolEffectShadowColor = nonEmptyString(content.sectionSymbolEffectShadowColor, '#130b05');
  const symbolEffectOuterGlowColor = nonEmptyString(content.sectionSymbolEffectOuterGlowColor, '#b9823c');
  const symbolEffectOuterGlowOpacity = clamp(finiteNumber(content.sectionSymbolEffectOuterGlowOpacity, 0.2), 0, 1);
  const symbolEffectLightAngle = clamp(finiteNumber(content.sectionSymbolEffectLightAngle, 315), 0, 360);
  const wallDepthEnabled =
    content.sectionWallDepthEnabled === undefined ? true : content.sectionWallDepthEnabled === true;
  const wallDepthStrength = clamp(finiteNumber(content.sectionWallDepthStrength, 0.38), 0, 1);
  const wallHighlightResponse = clamp(finiteNumber(content.sectionWallHighlightResponse, 0.62), 0, 1.5);
  const wallShadowResponse = clamp(finiteNumber(content.sectionWallShadowResponse, 0.38), 0, 1.2);
  const wallTextureReveal = clamp(finiteNumber(content.sectionWallLightTextureReveal, 0.54), 0, 1.5);
  const wallReliefSoftness = clamp(finiteNumber(content.sectionWallTextureReliefSoftness, 16), 0, 42);
  const wallLightColorMode = content.sectionWallLightColorMode === 'manual' ? 'manual' : 'colourway';
  const wallColourInfluence = clamp(
    finiteNumber(content.sectionWallLightColourInfluence, finiteNumber(content.sectionBacklightColourInfluence, 0.48)),
    0,
    1
  );
  const wallWarmColor =
    wallLightColorMode === 'manual'
      ? nonEmptyString(content.sectionWallLightWarmColor, '#d0a04f')
      : resolvePremiumGlowColor(primaryGlowColor, '#d0a04f', wallColourInfluence);
  const wallCoolColor =
    wallLightColorMode === 'manual'
      ? nonEmptyString(content.sectionWallLightCoolColor, '#7b58d6')
      : resolvePremiumGlowColor(secondaryGlowColor, '#7b58d6', wallColourInfluence);
  const wallLightX = clamp(finiteNumber(content.sectionWallLightX, finiteNumber(content.sectionBacklightX, 74)), 0, 100);
  const wallLightY = resolveAnchoredSceneY(content, 'sectionWallLightYOffset', 'sectionWallLightY', -24);
  const wallLightSpread = clamp(finiteNumber(content.sectionWallLightSpread, 72), 12, 150);
  const wallLightHeight = clamp(finiteNumber(content.sectionWallLightHeight, 54), 8, 120);
  const wallLightTextProtect = clamp(finiteNumber(content.sectionWallLightTextProtect, 0.72), 0, 1);
  const sceneHorizonY = resolveSceneHorizonY(content);
  const wallAboveMask = buildWallAboveMask(sceneHorizonY);
  const useUnifiedWallLightMask = isSectionBackdropQuarantined('unifiedWallLighting');
  const wallLightRevealMask = buildWallLightRevealMask({
    wallLightX,
    wallLightY,
    wallLightSpread,
    wallLightHeight,
    wallLightTextProtect,
    unifiedLightFalloff: useUnifiedWallLightMask,
  });
  const floorReflectionEnabled =
    content.sectionFloorArtReflectionEnabled === undefined
      ? true
      : content.sectionFloorArtReflectionEnabled === true;
  const floorReflectionOpacity = clamp(finiteNumber(content.sectionFloorArtReflectionOpacity, 0.32), 0, 1);
  const floorReflectionBlur = clamp(finiteNumber(content.sectionFloorArtReflectionBlur, 7), 0, 48);
  const floorReflectionDepth = clamp(finiteNumber(content.sectionFloorArtReflectionDepth, 54), 4, 86);
  const floorReflectionFade = clamp(finiteNumber(content.sectionFloorArtReflectionFade, 0.9), 0.12, 1);
  const symbolFloorReflectionAmount = legacySceneSymbolsEnabled
    ? clamp(
        finiteNumber(content.sectionSymbolFloorReflectionAmount, bakedSymbolsEnabled ? 0.2 : 0.16),
        0,
        1
      )
    : 0;
  const symbolRevealWidth = clamp(34 + symbolVignette * 58, 38, 118);
  const symbolRevealHeight = clamp(38 + symbolVignette * 72, 42, 142);
  const symbolRevealMask = `radial-gradient(ellipse ${symbolRevealWidth}% ${symbolRevealHeight}% at ${wallLightX}% ${wallLightY}%, black 0%, black 28%, rgba(0,0,0,0.58) 58%, transparent 92%)`;
  const symbolVisibilityMask = useUnifiedWallLightMask ? wallLightRevealMask : symbolRevealMask;

  const wallWrapperStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    isolation: 'isolate',
    contain: 'paint',
    maskImage: wallAboveMask,
    WebkitMaskImage: wallAboveMask,
  };

  const symbolWrapperStyle = applyCssMaskLayers(
    {
      position: 'absolute',
      inset: 0,
      isolation: 'isolate',
      contain: 'paint',
    },
    combineCssMasks(symbolVisibilityMask, wallAboveMask)
  );

  const textureStyle: CSSProperties | null = textureSrc
    ? applyCssMaskLayers(
        {
          backgroundImage: `linear-gradient(${tint}24, ${tint}24), url("${safeCssUrl(textureSrc)}")`,
          backgroundBlendMode: 'soft-light, normal',
          backgroundRepeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
          backgroundSize:
            textureRepeatMode === 'repeat'
              ? `${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
              : 'cover',
          backgroundPosition: `${x}% ${y}%`,
          mixBlendMode: blendMode,
          opacity: textureOpacity,
          filter: `brightness(${textureBrightness}) contrast(${textureContrast})`,
        },
        wallLightRevealMask
      )
    : null;

  const textureDepthShadowStyle: CSSProperties | null =
    textureSrc && wallDepthEnabled && wallDepthStrength > 0 && wallShadowResponse > 0
      ? applyCssMaskLayers(
          {
            backgroundImage: `url("${safeCssUrl(textureSrc)}")`,
            backgroundRepeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
            backgroundSize:
              textureRepeatMode === 'repeat'
                ? `${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
                : 'cover',
            backgroundPosition: `${x + 0.35}% ${y + 0.4}%`,
            mixBlendMode: 'multiply',
            opacity: clamp(textureOpacity * wallDepthStrength * wallShadowResponse * 1.5, 0, 0.26),
            filter: `brightness(0.52) contrast(${1.05 + textureContrast * 0.2}) blur(${Math.max(0, wallReliefSoftness * 0.05)}px)`,
          },
          wallLightRevealMask
        )
      : null;

  const textureDepthHighlightStyle: CSSProperties | null =
    textureSrc && wallDepthEnabled && wallDepthStrength > 0 && wallHighlightResponse > 0
      ? applyCssMaskLayers(
          {
            backgroundImage: [
              `linear-gradient(115deg, transparent 0%, ${wallWarmColor}26 38%, ${wallCoolColor}18 62%, transparent 100%)`,
              `url("${safeCssUrl(textureSrc)}")`,
            ].join(', '),
            backgroundBlendMode: 'screen, normal',
            backgroundRepeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
            backgroundSize:
              textureRepeatMode === 'repeat'
                ? `${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
                : 'cover',
            backgroundPosition: `${x - 0.25}% ${y - 0.35}%`,
            mixBlendMode: 'soft-light',
            opacity: clamp(textureOpacity * wallDepthStrength * wallHighlightResponse * 1.8, 0, 0.38),
            filter: `brightness(${1.05 + wallHighlightResponse * 0.26}) contrast(${1.05 + textureContrast * 0.18}) blur(${Math.max(0, wallReliefSoftness * 0.035)}px)`,
          },
          wallLightRevealMask
        )
      : null;

  const textureLightRevealStyle: CSSProperties | null =
    textureSrc && wallDepthEnabled && wallTextureReveal > 0
      ? applyCssMaskLayers(
          {
            backgroundImage: [
              `radial-gradient(ellipse ${wallLightSpread * 0.55}% ${wallLightHeight * 0.9}% at ${wallLightX - wallLightSpread * 0.12}% ${wallLightY}%, ${wallWarmColor}38 0%, ${wallWarmColor}18 36%, transparent 78%)`,
              `radial-gradient(ellipse ${wallLightSpread * 0.48}% ${wallLightHeight * 0.86}% at ${wallLightX + wallLightSpread * 0.16}% ${wallLightY + 4}%, ${wallCoolColor}30 0%, ${wallCoolColor}14 36%, transparent 78%)`,
              `url("${safeCssUrl(textureSrc)}")`,
            ].join(', '),
            backgroundBlendMode: 'screen, screen, soft-light',
            backgroundRepeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
            backgroundSize:
              textureRepeatMode === 'repeat'
                ? `100% 100%, 100% 100%, ${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
                : '100% 100%, 100% 100%, cover',
            backgroundPosition: `center, center, ${x}% ${y}%`,
            mixBlendMode: 'screen',
            opacity: clamp(textureOpacity * wallTextureReveal * 1.35, 0, 0.34),
            filter: `blur(${wallReliefSoftness * 0.08}px) brightness(1.08) contrast(${1 + wallTextureReveal * 0.12})`,
          },
          wallLightRevealMask
        )
      : null;

  const sharedSymbolMask = symbolSrc ? symbolMaskStyle(symbolSrc, symbolScale, symbolX, symbolY) : null;
  const symbolBakedOverlayStyle: CSSProperties | null = bakedSymbolSrc
    ? {
        backgroundImage: `url("${safeCssUrl(bakedSymbolSrc)}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${symbolScale}% auto`,
        backgroundPosition: `${symbolX}% ${symbolY}%`,
        mixBlendMode: symbolBlendMode === 'multiply' ? 'soft-light' : symbolBlendMode,
        opacity: symbolOpacity,
        filter: `brightness(${1 + symbolEffectStrength * 0.08}) contrast(${1 + symbolEffectStrength * 0.12}) blur(${Math.max(0, symbolEffectSoftness * 0.035)}px)`,
        transform: 'translateZ(0)',
        willChange: 'opacity, filter, transform',
      }
    : null;

  const floorZoneHeight = clamp(100 - sceneHorizonY, 20, 100);
  const floorReflectionMask = [
    `linear-gradient(180deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,${0.58 * floorReflectionFade}) 22%, rgba(0,0,0,${0.22 * floorReflectionFade}) 58%, rgba(0,0,0,0.08) 84%, transparent 100%)`,
    `radial-gradient(ellipse ${Math.max(40, wallLightSpread * 0.9)}% 96% at ${wallLightX}% 0%, black 0%, black 42%, transparent 100%)`,
  ].join(', ');

  const floorReflectionStyle: CSSProperties | null = isSectionBackdropQuarantined(
    'cssWallArtFloorReflection'
  )
    ? null
    : textureSrc && floorReflectionEnabled && floorReflectionOpacity > 0
      ? {
          left: 0,
          right: 0,
          top: `${sceneHorizonY}%`,
          height: `${floorZoneHeight}%`,
          opacity: floorReflectionOpacity,
          backgroundImage: [
            `linear-gradient(180deg, ${tint}20 0%, ${tint}08 38%, transparent 100%)`,
            `url("${safeCssUrl(textureSrc)}")`,
          ].join(', '),
          backgroundBlendMode: 'overlay, normal',
          backgroundRepeat: textureRepeatMode === 'repeat' ? 'repeat' : 'no-repeat',
          backgroundSize:
            textureRepeatMode === 'repeat'
              ? `${Math.max(620, textureScale * 6.4)}px ${Math.max(360, textureScale * 3.8)}px`
              : 'cover',
          backgroundPosition: `${x}% ${100 - y}%`,
          filter: `blur(${floorReflectionBlur}px) brightness(0.82) contrast(${1.08 + textureContrast * 0.1})`,
          mixBlendMode: 'soft-light',
          maskImage: floorReflectionMask,
          WebkitMaskImage: floorReflectionMask,
          transform: 'translateZ(0)',
        }
      : null;

  const symbolAuraStyle: CSSProperties | null = sharedSymbolMask
    ? {
        ...sharedSymbolMask,
        backgroundColor: symbolEffectPreset === 'raised-gilded' ? symbolEffectOuterGlowColor : symbolTint,
        mixBlendMode: 'soft-light',
        opacity: clamp(
          symbolOpacity *
            (0.14 + clamp((symbolLineWeight - 0.5) / 2.5, 0, 1) * 0.28 + symbolEffectStrength * 0.08),
          0,
          0.34
        ),
        filter: `blur(${clamp(2.5 + symbolLineWeight * 1.45 + symbolEffectSoftness * 0.22, 2.5, 12)}px)`,
        transform: `scale(${1.006 + clamp((symbolLineWeight - 0.5) / 2.5, 0, 1) * 0.018}) translateZ(0)`,
        transformOrigin: `${symbolX}% ${symbolY}%`,
      }
    : null;

  const symbolLightReactiveStyle: CSSProperties | null = sharedSymbolMask
    ? {
        ...sharedSymbolMask,
        backgroundImage: [
          `radial-gradient(ellipse ${Math.max(36, wallLightSpread * 0.58)}% ${Math.max(32, wallLightHeight * 0.92)}% at ${wallLightX}% ${wallLightY}%, ${wallWarmColor}cc 0%, ${wallWarmColor}74 30%, ${wallCoolColor}44 54%, transparent 82%)`,
          `radial-gradient(ellipse ${Math.max(24, wallLightSpread * 0.34)}% ${Math.max(20, wallLightHeight * 0.48)}% at ${clamp(wallLightX + 8, 0, 100)}% ${clamp(wallLightY + 4, 0, 100)}%, ${wallCoolColor}88 0%, transparent 74%)`,
        ].join(', '),
        mixBlendMode: 'screen',
        opacity: clamp(
          symbolOpacity * (0.12 + symbolEffectStrength * 0.34) * Math.max(0.25, symbolEffectOuterGlowOpacity),
          0,
          0.36
        ),
        filter: `blur(${clamp(0.8 + symbolEffectSoftness * 0.45, 0.8, 7)}px)`,
        transform: 'translateZ(0)',
        transformOrigin: `${symbolX}% ${symbolY}%`,
      }
    : null;

  const reliefOffset = offsetByAngle(symbolEffectLightAngle, symbolEffectDistance * 0.018);
  const reliefBlur = Math.max(0, symbolEffectSoftness);
  const lineWeightExtra = Math.max(0, symbolLineWeight - 1);
  const lineWeightOpacity = symbolOpacity * clamp(lineWeightExtra * 0.08, 0, 0.18);
  const lineWeightBlur = 1.1 + lineWeightExtra * 0.9;
  const lineBodyBlur = 0.45 + reliefBlur * 0.35;
  const lineSoftBlur = 1.25 + reliefBlur * 0.75;
  const fineEdgeBlur = Math.max(0, reliefBlur * 0.16);
  const symbolEffectLayerStyles: CSSProperties[] = [];
  const addSymbolLayer = ({
    offsetX = 0,
    offsetY = 0,
    color,
    opacity: layerOpacity,
    blendMode: layerBlendMode,
    blur = reliefBlur,
    scale: layerScale = 1,
  }: {
    offsetX?: number;
    offsetY?: number;
    color: string;
    opacity: number;
    blendMode: CSSProperties['mixBlendMode'];
    blur?: number;
    scale?: number;
  }) => {
    if (!sharedSymbolMask || !symbolSrc || layerOpacity <= 0) return;
    symbolEffectLayerStyles.push({
      ...symbolMaskStyle(symbolSrc, symbolScale, symbolX + offsetX, symbolY + offsetY),
      backgroundColor: color,
      mixBlendMode: layerBlendMode,
      opacity: clamp(layerOpacity, 0, 1),
      filter: blur > 0 ? `blur(${blur}px)` : undefined,
      transform: layerScale === 1 ? 'translateZ(0)' : `scale(${layerScale}) translateZ(0)`,
      transformOrigin: `${symbolX}% ${symbolY}%`,
    });
  };

  if (symbolLightReactiveStyle) {
    symbolEffectLayerStyles.push(symbolLightReactiveStyle);
  }

  if (sharedSymbolMask && lineWeightExtra > 0 && lineWeightOpacity > 0) {
    addSymbolLayer({
      color: symbolTint,
      opacity: lineWeightOpacity,
      blendMode: 'soft-light',
      blur: lineWeightBlur,
      scale: 1 + lineWeightExtra * 0.006,
    });
  }

  if (sharedSymbolMask && symbolEffectStrength > 0) {
    if (symbolEffectPreset === 'soft-glow') {
      addSymbolLayer({
        color: symbolEffectOuterGlowColor,
        opacity: symbolOpacity * symbolEffectStrength * clamp(symbolEffectOuterGlowOpacity, 0.08, 1) * 0.58,
        blendMode: 'screen',
        blur: 10 + reliefBlur * 1.1,
        scale: 1.018,
      });
      addSymbolLayer({
        color: symbolTint,
        opacity: symbolOpacity * symbolEffectStrength * 0.34,
        blendMode: 'screen',
        blur: lineBodyBlur,
        scale: 1.006,
      });
    } else if (symbolEffectPreset === 'embossed-wall') {
      addSymbolLayer({
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.58,
        blendMode: 'multiply',
        blur: lineSoftBlur,
        scale: 1.014,
      });
      addSymbolLayer({
        color: symbolTint,
        opacity: symbolOpacity * symbolEffectStrength * 0.42,
        blendMode: 'soft-light',
        blur: lineBodyBlur,
        scale: 1.008,
      });
      addSymbolLayer({
        offsetX: reliefOffset.x,
        offsetY: reliefOffset.y,
        color: symbolEffectHighlightColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.44,
        blendMode: 'screen',
        blur: fineEdgeBlur,
      });
      addSymbolLayer({
        offsetX: -reliefOffset.x,
        offsetY: -reliefOffset.y,
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.46,
        blendMode: 'multiply',
        blur: fineEdgeBlur + 0.15,
      });
    } else if (symbolEffectPreset === 'engraved-pressed') {
      addSymbolLayer({
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.48,
        blendMode: 'multiply',
        blur: lineSoftBlur,
        scale: 1.01,
      });
      addSymbolLayer({
        color: symbolTint,
        opacity: symbolOpacity * symbolEffectStrength * 0.2,
        blendMode: 'multiply',
        blur: lineBodyBlur,
        scale: 1.004,
      });
      addSymbolLayer({
        offsetX: -reliefOffset.x,
        offsetY: -reliefOffset.y,
        color: symbolEffectHighlightColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.18,
        blendMode: 'soft-light',
        blur: fineEdgeBlur,
      });
      addSymbolLayer({
        offsetX: reliefOffset.x,
        offsetY: reliefOffset.y,
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.26,
        blendMode: 'multiply',
        blur: fineEdgeBlur + 0.1,
      });
    } else if (symbolEffectPreset === 'raised-gilded') {
      addSymbolLayer({
        color: symbolEffectOuterGlowColor,
        opacity: symbolOpacity * symbolEffectStrength * clamp(symbolEffectOuterGlowOpacity, 0.08, 1) * 0.48,
        blendMode: 'screen',
        blur: 7 + reliefBlur,
        scale: 1.018,
      });
      addSymbolLayer({
        color: symbolEffectOuterGlowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.36,
        blendMode: 'screen',
        blur: lineSoftBlur,
        scale: 1.012,
      });
      addSymbolLayer({
        color: symbolTint,
        opacity: symbolOpacity * symbolEffectStrength * 0.3,
        blendMode: 'soft-light',
        blur: lineBodyBlur,
        scale: 1.006,
      });
      addSymbolLayer({
        offsetX: -reliefOffset.x,
        offsetY: -reliefOffset.y,
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.26,
        blendMode: 'multiply',
        blur: fineEdgeBlur + 0.15,
      });
      addSymbolLayer({
        offsetX: reliefOffset.x,
        offsetY: reliefOffset.y,
        color: symbolEffectHighlightColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.3,
        blendMode: 'screen',
        blur: fineEdgeBlur,
      });
    } else if (symbolEffectPreset === 'ancient-carved') {
      addSymbolLayer({
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.58,
        blendMode: 'multiply',
        blur: lineSoftBlur * 0.85,
        scale: 1.012,
      });
      addSymbolLayer({
        color: symbolTint,
        opacity: symbolOpacity * symbolEffectStrength * 0.44,
        blendMode: 'soft-light',
        blur: lineBodyBlur * 0.75,
        scale: 1.006,
      });
      addSymbolLayer({
        offsetX: reliefOffset.x,
        offsetY: reliefOffset.y,
        color: symbolEffectHighlightColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.4,
        blendMode: 'screen',
        blur: fineEdgeBlur,
      });
      addSymbolLayer({
        offsetX: -reliefOffset.x,
        offsetY: -reliefOffset.y,
        color: symbolEffectShadowColor,
        opacity: symbolOpacity * symbolEffectStrength * 0.42,
        blendMode: 'multiply',
        blur: fineEdgeBlur + 0.1,
      });
    }
  }

  const symbolFloorReflectionStyle: CSSProperties | null = isSectionBackdropQuarantined(
    'cssSymbolFloorReflection'
  )
    ? null
    : bakedSymbolSrc && floorReflectionEnabled && symbolFloorReflectionAmount > 0 && floorReflectionOpacity > 0
      ? {
          left: 0,
          right: 0,
          top: `${sceneHorizonY}%`,
          height: `${clamp(floorReflectionDepth * 1.04, 8, floorZoneHeight)}%`,
          backgroundImage: `url("${safeCssUrl(bakedSymbolSrc)}")`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${symbolScale}% auto`,
          backgroundPosition: `${symbolX}% ${clamp(100 - symbolY + floorReflectionDepth * 0.06, 52, 100)}%`,
          mixBlendMode: symbolBlendMode === 'multiply' ? 'soft-light' : symbolBlendMode,
          opacity: clamp(symbolOpacity * symbolFloorReflectionAmount * 0.78, 0, 0.34),
          filter: `blur(${Math.max(4, floorReflectionBlur * 0.85)}px) brightness(0.72) contrast(1.08)`,
          maskImage: floorReflectionMask,
          WebkitMaskImage: floorReflectionMask,
          transform: 'scaleY(-1) translateZ(0)',
          transformOrigin: 'center top',
        }
      : sharedSymbolMask && floorReflectionEnabled && symbolFloorReflectionAmount > 0 && floorReflectionOpacity > 0
        ? {
            left: 0,
            right: 0,
            top: `${sceneHorizonY}%`,
            height: `${floorReflectionDepth * 1.04}%`,
            backgroundImage: `linear-gradient(180deg, ${symbolTint}72 0%, ${symbolTint}22 42%, transparent 100%)`,
            mixBlendMode: symbolBlendMode === 'multiply' ? 'soft-light' : symbolBlendMode,
            opacity: clamp(symbolOpacity * symbolFloorReflectionAmount * 0.82, 0, 0.32),
            filter: `blur(${Math.max(4, floorReflectionBlur * 0.72)}px)`,
            transform: 'translateZ(0)',
            ...symbolMaskStyle(symbolSrc, symbolScale, symbolX, 100 - symbolY),
          }
        : null;

  const symbolStyle: CSSProperties | null = sharedSymbolMask
    ? {
        ...sharedSymbolMask,
        backgroundColor: symbolTint,
        mixBlendMode: symbolBlendMode === 'multiply' ? 'soft-light' : symbolBlendMode,
        opacity: clamp(
          symbolOpacity *
            (symbolEffectPreset === 'engraved-pressed'
              ? 0.42
              : symbolEffectPreset === 'raised-gilded'
                ? 0.56
                : 0.5),
          0,
          0.42
        ),
        filter: 'blur(0.12px)',
      }
    : null;

  return {
    wallWrapperStyle,
    symbolWrapperStyle,
    textureStyle,
    textureDepthShadowStyle,
    textureDepthHighlightStyle,
    textureLightRevealStyle,
    floorReflectionStyle,
    symbolFloorReflectionStyle,
    symbolAuraStyle,
    symbolEffectLayerStyles,
    symbolStyle,
    symbolBakedOverlayStyle,
  };
}
